import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

interface GaugeChartProps {
  value: number;
  min?: number;
  max?: number;
  size?: number;
  label?: string;
  unit?: string;
  zones?: { threshold: number; color: string }[];
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  min = 0,
  max = 100,
  size = 200,
  label = '',
  unit = '',
  zones = [
    { threshold: 33, color: '#EF4444' },
    { threshold: 66, color: '#F59E0B' },
    { threshold: 100, color: '#10B981' },
  ],
}) => {
  const [animatedValue, setAnimatedValue] = useState(min);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  // Clamp percentage between 0 and 100
  const percentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(value);
      setAnimatedPercentage(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [value, percentage]);

  // Determine current zone color
  const getCurrentColor = () => {
    for (const zone of zones) {
      if (percentage <= zone.threshold) {
        return zone.color;
      }
    }
    return zones[zones.length - 1].color;
  };

  const currentColor = getCurrentColor();

  // Gauge configuration for semi-circle (180 degrees)
  const strokeWidth = 20;
  const radius = (size - strokeWidth * 2) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  // Semi-circle starts at bottom-left (-90°) and ends at bottom-right (90°)
  // In SVG: -90° = 270°, 90° = 90°
  const startAngle = -90; // degrees
  const endAngle = 90; // degrees
  const angleRange = endAngle - startAngle; // 180 degrees

  // Calculate the angle for the current percentage
  const currentAngle = startAngle + (animatedPercentage / 100) * angleRange;

  // Convert to radians for calculations
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;
  const currentRad = (currentAngle * Math.PI) / 180;

  // Create arc path
  const createArcPath = (startAngle: number, endAngle: number) => {
    const start = (startAngle * Math.PI) / 180;
    const end = (endAngle * Math.PI) / 180;

    const startX = centerX + radius * Math.cos(start);
    const startY = centerY + radius * Math.sin(start);
    const endX = centerX + radius * Math.cos(end);
    const endY = centerY + radius * Math.sin(end);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  };

  // Needle endpoint
  const needleLength = radius - 10;
  const needleX = centerX + needleLength * Math.cos(currentRad);
  const needleY = centerY + needleLength * Math.sin(currentRad);

  return (
    <div
      className="flex flex-col items-center"
      style={{ width: size, height: 'auto' }}
    >
      <svg
        width={size}
        height={size / 2 + 40}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <filter id={`gauge-glow-${label}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background arc - full semi-circle in gray */}
        <path
          d={createArcPath(startAngle, endAngle)}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Colored progress arc - animates from 0% to current percentage */}
        <motion.path
          d={createArcPath(startAngle, startAngle)}
          animate={{ d: createArcPath(startAngle, currentAngle) }}
          fill="none"
          stroke={currentColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          transition={{ duration: 1.5, ease: 'easeOut' }}
          filter={`url(#gauge-glow-${label})`}
        />

        {/* Needle */}
        <motion.line
          x1={centerX}
          y1={centerY}
          x2={centerX + needleLength * Math.cos(startRad)}
          y2={centerY + needleLength * Math.sin(startRad)}
          animate={{
            x2: needleX,
            y2: needleY,
          }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          stroke="#374151"
          strokeWidth={3}
          strokeLinecap="round"
        />

        {/* Needle tip circle */}
        <motion.circle
          cx={centerX + needleLength * Math.cos(startRad)}
          cy={centerY + needleLength * Math.sin(startRad)}
          animate={{
            cx: needleX,
            cy: needleY,
          }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          r={6}
          fill={currentColor}
          filter={`url(#gauge-glow-${label})`}
        />

        {/* Center pivot */}
        <circle
          cx={centerX}
          cy={centerY}
          r={10}
          fill="#374151"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={5}
          fill="white"
        />

        {/* Min label */}
        <text
          x={centerX + radius * Math.cos(startRad) - 10}
          y={centerY + radius * Math.sin(startRad) + 20}
          fontSize="12"
          fill="#9CA3AF"
          textAnchor="start"
          fontWeight="500"
        >
          {min}
        </text>

        {/* Max label */}
        <text
          x={centerX + radius * Math.cos(endRad) + 10}
          y={centerY + radius * Math.sin(endRad) + 20}
          fontSize="12"
          fill="#9CA3AF"
          textAnchor="end"
          fontWeight="500"
        >
          {max}
        </text>
      </svg>

      {/* Value display */}
      <div className="text-center mt-2">
        <div className="text-3xl font-bold" style={{ color: currentColor }}>
          <CountUp end={animatedValue} duration={1.5} decimals={value % 1 !== 0 ? 1 : 0} />
          {unit && <span className="text-xl ml-1">{unit}</span>}
        </div>
        {label && <div className="text-sm text-gray-500 mt-1">{label}</div>}
      </div>
    </div>
  );
};

export default GaugeChart;
